import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Archive, 
  Upload, 
  X, 
  CheckCircle, 
  AlertCircle,
  FileText,
  BookOpen,
  Zap,
  FileCheck,
  FolderOpen,
  Eye,
  Trash2,
  Plus,
  RefreshCw,
  Clock,
  Check,
  XCircle,
  Layers,
  GraduationCap,
  Book,
  Tag,
  Edit,
  Link2,
  ArrowRight,
  Save,
  Calendar
} from 'lucide-react';

function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState(false);
  
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'yahyaoui1!';  
  // Data states
  const [programs, setPrograms] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [levels, setLevels] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [resources, setResources] = useState([]);
  const [pendingResources, setPendingResources] = useState([]);
  const [approvedResources, setApprovedResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Cascade states for resource form
  const [availableDegrees, setAvailableDegrees] = useState([]);
  const [availableLevels, setAvailableLevels] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  
  // Modal states
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [showEditResourceModal, setShowEditResourceModal] = useState(false);
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [showEditSubjectModal, setShowEditSubjectModal] = useState(false);
  const [showLevelModal, setShowLevelModal] = useState(false);
  const [showEditLevelModal, setShowEditLevelModal] = useState(false);
  const [showDegreeModal, setShowDegreeModal] = useState(false);
  const [showEditDegreeModal, setShowEditDegreeModal] = useState(false);
  const [showProgramModal, setShowProgramModal] = useState(false);
  const [showEditProgramModal, setShowEditProgramModal] = useState(false);
  const [showLinkCorrectionModal, setShowLinkCorrectionModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [selectedDegree, setSelectedDegree] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [formSuccess, setFormSuccess] = useState('');
  const [formError, setFormError] = useState('');
  
  // Form states
  const [resourceForm, setResourceForm] = useState({ 
    title: '', 
    type: 'Cours', 
    programId: '',
    degreeId: '',
    levelId: '',
    subjectId: '', 
    file: null
  });
  
  const [editForm, setEditForm] = useState({ 
    title: '', 
    type: 'Cours', 
    subjectId: '', 
    file: null
  });
  
  const [subjectForm, setSubjectForm] = useState({ name: '', levelId: '', semester: 'Semestre 1' });
  const [levelForm, setLevelForm] = useState({ name: '', degreeId: '', order: 1 });
  const [degreeForm, setDegreeForm] = useState({ name: '', programId: '', order: 1 });
  const [programForm, setProgramForm] = useState({ name: '', order: 1 });
  const [linkForm, setLinkForm] = useState({ sourceId: '', targetId: '' });

  // Cascade states for Subject Form
  const [subjectLevels, setSubjectLevels] = useState([]);
  
  // Cascade states for Level Form
  const [levelDegrees, setLevelDegrees] = useState([]);

  const resourceTypes = [
    { id: 'Cours', name: 'Cours', icon: <BookOpen size={16} />, color: 'mint', description: 'Documents de cours, supports pédagogiques' },
    { id: 'Examens', name: 'Examens', icon: <FileText size={16} />, color: 'pink', description: 'Sujets d\'examens, corrections' },
    { id: 'Correction', name: 'Correction', icon: <CheckCircle size={16} />, color: 'yellow', description: 'Corrections d\'examens, TD, TP' },
    { id: 'TP', name: 'TP', icon: <Zap size={16} />, color: 'yellow', description: 'Travaux pratiques, exercices' },
    { id: 'TD', name: 'TD', icon: <FileCheck size={16} />, color: 'orange', description: 'Travaux dirigés, séries' },
    { id: 'Projets', name: 'Projets', icon: <FolderOpen size={16} />, color: 'purple', description: 'Projets, rapports, présentations' }
  ];

  const typesThatCanHaveCorrection = ['Examens', 'TD', 'TP'];

  // Fetch all data
  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      const [programsRes, degreesRes, levelsRes, subjectsRes, resourcesRes] = await Promise.all([
        fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/programs'),
        fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/degrees?populate=program'),
        fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/levels?populate=degree'),
        fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/subjects?populate=level'),
        fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/resources')
      ]);
      
      const programsData = await programsRes.json();
      const degreesData = await degreesRes.json();
      const levelsData = await levelsRes.json();
      const subjectsData = await subjectsRes.json();
      const resourcesData = await resourcesRes.json();
      
      setPrograms(programsData.data || []);
      setDegrees(degreesData.data || []);
      setLevels(levelsData.data || []);
      setSubjects(subjectsData.data || []);
      
      const allResources = resourcesData.data || [];
      const subjectsMap = {};
      (subjectsData.data || []).forEach(s => {
        subjectsMap[s.id] = s;
      });
      
      const resourcesWithSubjects = allResources.map(resource => ({
        ...resource,
        subjectName: subjectsMap[resource.subject]?.name || 'N/A',
        subjectObj: subjectsMap[resource.subject] || null
      }));
      
      const pending = resourcesWithSubjects.filter(r => r.approvalStatus === 'pending');
      const approved = resourcesWithSubjects.filter(r => r.approvalStatus === 'approved');
      
      setPendingResources(pending);
      setApprovedResources(approved);
      setResources(resourcesWithSubjects);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setFormError('Erreur de connexion à Strapi');
      setTimeout(() => setFormError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Cascade for Resource Form
  useEffect(() => {
    if (resourceForm.programId && resourceForm.programId !== '') {
      const programIdNum = parseInt(resourceForm.programId);
      const filtered = degrees.filter(d => d.program?.id === programIdNum);
      setAvailableDegrees(filtered);
    } else {
      setAvailableDegrees([]);
    }
    setResourceForm(prev => ({ ...prev, degreeId: '', levelId: '', subjectId: '' }));
  }, [resourceForm.programId, degrees]);

  useEffect(() => {
    if (resourceForm.degreeId && resourceForm.degreeId !== '') {
      const degreeIdNum = parseInt(resourceForm.degreeId);
      const filtered = levels.filter(l => l.degree?.id === degreeIdNum);
      setAvailableLevels(filtered);
    } else {
      setAvailableLevels([]);
    }
    setResourceForm(prev => ({ ...prev, levelId: '', subjectId: '' }));
  }, [resourceForm.degreeId, levels]);

  useEffect(() => {
    if (resourceForm.levelId && resourceForm.levelId !== '') {
      const levelIdNum = parseInt(resourceForm.levelId);
      const filtered = subjects.filter(s => s.level?.id === levelIdNum);
      setAvailableSubjects(filtered);
    } else {
      setAvailableSubjects([]);
    }
    setResourceForm(prev => ({ ...prev, subjectId: '' }));
  }, [resourceForm.levelId, subjects]);

  // Cascade for Subject Form
  useEffect(() => {
    if (subjectForm.levelId && subjectForm.levelId !== '') {
      const filtered = levels.filter(l => l.id === parseInt(subjectForm.levelId));
      setSubjectLevels(filtered);
    }
  }, [subjectForm.levelId, levels]);

  // Cascade for Level Form
  useEffect(() => {
    if (levelForm.degreeId && levelForm.degreeId !== '') {
      const filtered = degrees.filter(d => d.id === parseInt(levelForm.degreeId));
      setLevelDegrees(filtered);
    }
  }, [levelForm.degreeId, degrees]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setLoginError(false);
      fetchAllData();
    } else {
      setLoginError(true);
    }
  };

  // ===== RESOURCE CRUD =====
  const handleAddResource = async (e) => {
    e.preventDefault();
    if (!resourceForm.title || !resourceForm.subjectId || !resourceForm.file) {
      setFormError('Titre, matière et fichier requis');
      setTimeout(() => setFormError(''), 3000);
      return;
    }
    
    try {
      setLoading(true);
      
      const uploadFormData = new FormData();
      uploadFormData.append('files', resourceForm.file);
      
      const uploadRes = await fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/upload', {
        method: 'POST',
        body: uploadFormData
      });
      
      const uploadData = await uploadRes.json();
      if (!uploadRes.ok) throw new Error('Upload failed');
      
      const fileId = uploadData[0].id;
      
      const selectedSubject = subjects.find(s => s.id === parseInt(resourceForm.subjectId));
      if (!selectedSubject) throw new Error('Matière non trouvée');
      
      const resourceData = {
        data: {
          title: resourceForm.title,
          type: resourceForm.type,
          approvalStatus: 'approved',
          file: fileId,
          subject: parseInt(resourceForm.subjectId)
        }
      };
      
      const createRes = await fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/resources', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(resourceData)
      });
      
      if (createRes.ok) {
        setFormSuccess(`Ressource "${resourceForm.title}" ajoutée avec succès`);
        setResourceForm({ title: '', type: 'Cours', programId: '', degreeId: '', levelId: '', subjectId: '', file: null });
        setShowResourceModal(false);
        fetchAllData();
        setTimeout(() => setFormSuccess(''), 3000);
      } else {
        throw new Error("Erreur lors de l'ajout");
      }
    } catch (error) {
      console.error('Error:', error);
      setFormError(error.message || "Erreur de connexion");
      setTimeout(() => setFormError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateResource = async (e) => {
    e.preventDefault();
    if (!selectedResource) return;
    
    try {
      setLoading(true);
      
      const updateData = { data: {} };
      
      if (editForm.title !== selectedResource.title) {
        updateData.data.title = editForm.title;
      }
      
      if (editForm.type !== selectedResource.type) {
        updateData.data.type = editForm.type;
      }
      
      if (editForm.subjectId && parseInt(editForm.subjectId) !== selectedResource.subject) {
        updateData.data.subject = parseInt(editForm.subjectId);
      }
      
      if (editForm.file) {
        const uploadFormData = new FormData();
        uploadFormData.append('files', editForm.file);
        
        const uploadRes = await fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/upload', {
          method: 'POST',
          body: uploadFormData
        });
        
        const uploadData = await uploadRes.json();
        if (uploadRes.ok) {
          updateData.data.file = uploadData[0].id;
        }
      }
      
      if (Object.keys(updateData.data).length === 0) {
        setFormError('Aucune modification détectée');
        setTimeout(() => setFormError(''), 3000);
        return;
      }
      
      const response = await fetch(`https://unwavering-luck-2076e754ea.strapiapp.com/api/resources/${selectedResource.documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      
      if (response.ok) {
        setFormSuccess(`Ressource mise à jour avec succès`);
        setShowEditResourceModal(false);
        setSelectedResource(null);
        fetchAllData();
        setTimeout(() => setFormSuccess(''), 3000);
      } else {
        throw new Error("Erreur lors de la mise à jour");
      }
    } catch (error) {
      console.error('Error:', error);
      setFormError(error.message || "Erreur de connexion");
      setTimeout(() => setFormError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteResource = async (documentId) => {
    if (!window.confirm('Supprimer cette ressource définitivement ?')) return;
    try {
      await fetch(`https://unwavering-luck-2076e754ea.strapiapp.com/api/resources/${documentId}`, { method: 'DELETE' });
      setFormSuccess('Ressource supprimée');
      fetchAllData();
      setTimeout(() => setFormSuccess(''), 2000);
    } catch (error) {
      setFormError("Erreur lors de la suppression");
      setTimeout(() => setFormError(''), 3000);
    }
  };

  // ===== SUBJECT CRUD =====
  const handleAddSubject = async (e) => {
    e.preventDefault();
    if (!subjectForm.name || !subjectForm.levelId) {
      setFormError('Nom et niveau requis');
      setTimeout(() => setFormError(''), 3000);
      return;
    }
    
    try {
      setLoading(true);
      
      const selectedLevel = levels.find(l => l.id === parseInt(subjectForm.levelId));
      if (!selectedLevel) throw new Error('Niveau non trouvé');
      
      const response = await fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/subjects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            name: subjectForm.name,
            semester: subjectForm.semester,
            level: {
              connect: [selectedLevel.documentId]
            }
          }
        })
      });
      
      if (response.ok) {
        setFormSuccess(`Matière "${subjectForm.name}" ajoutée`);
        setSubjectForm({ name: '', levelId: '', semester: 'Semestre 1' });
        setShowSubjectModal(false);
        fetchAllData();
        setTimeout(() => setFormSuccess(''), 3000);
      } else {
        const error = await response.json();
        throw new Error(error.error?.message || "Erreur lors de l'ajout");
      }
    } catch (error) {
      setFormError(error.message);
      setTimeout(() => setFormError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubject = async (e) => {
    e.preventDefault();
    if (!selectedSubject) return;
    
    try {
      setLoading(true);
      
      const selectedLevel = levels.find(l => l.id === parseInt(subjectForm.levelId));
      if (!selectedLevel) throw new Error('Niveau non trouvé');
      
      const response = await fetch(`https://unwavering-luck-2076e754ea.strapiapp.com/api/subjects/${selectedSubject.documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            name: subjectForm.name,
            semester: subjectForm.semester,
            level: {
              connect: [selectedLevel.documentId]
            }
          }
        })
      });
      
      if (response.ok) {
        setFormSuccess(`Matière "${subjectForm.name}" mise à jour`);
        setShowEditSubjectModal(false);
        setSelectedSubject(null);
        fetchAllData();
        setTimeout(() => setFormSuccess(''), 3000);
      } else {
        const error = await response.json();
        throw new Error(error.error?.message || "Erreur lors de la mise à jour");
      }
    } catch (error) {
      setFormError(error.message);
      setTimeout(() => setFormError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubject = async (documentId, subjectName) => {
    if (!window.confirm(`Supprimer la matière "${subjectName}" ? Toutes les ressources associées perdront leur relation.`)) return;
    try {
      await fetch(`https://unwavering-luck-2076e754ea.strapiapp.com/api/subjects/${documentId}`, { method: 'DELETE' });
      setFormSuccess(`Matière "${subjectName}" supprimée`);
      fetchAllData();
      setTimeout(() => setFormSuccess(''), 2000);
    } catch (error) {
      setFormError("Erreur lors de la suppression");
      setTimeout(() => setFormError(''), 3000);
    }
  };

  const openEditSubjectModal = (subject) => {
    setSelectedSubject(subject);
    setSubjectForm({
      name: subject.name,
      levelId: subject.level?.id?.toString() || '',
      semester: subject.semester || 'Semestre 1'
    });
    setShowEditSubjectModal(true);
  };

  // ===== LEVEL CRUD =====
  const handleAddLevel = async (e) => {
    e.preventDefault();
    if (!levelForm.name || !levelForm.degreeId) {
      setFormError('Nom et département requis');
      setTimeout(() => setFormError(''), 3000);
      return;
    }
    
    try {
      setLoading(true);
      
      const selectedDegree = degrees.find(d => d.id === parseInt(levelForm.degreeId));
      if (!selectedDegree) throw new Error('Département non trouvé');
      
      const response = await fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/levels', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            name: levelForm.name,
            order: levelForm.order,
            degree: {
              connect: [selectedDegree.documentId]
            }
          }
        })
      });
      
      if (response.ok) {
        setFormSuccess(`Niveau "${levelForm.name}" ajouté`);
        setLevelForm({ name: '', degreeId: '', order: 1 });
        setShowLevelModal(false);
        fetchAllData();
        setTimeout(() => setFormSuccess(''), 3000);
      } else {
        const error = await response.json();
        throw new Error(error.error?.message || "Erreur lors de l'ajout");
      }
    } catch (error) {
      setFormError(error.message);
      setTimeout(() => setFormError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateLevel = async (e) => {
    e.preventDefault();
    if (!selectedLevel) return;
    
    try {
      setLoading(true);
      
      const selectedDegree = degrees.find(d => d.id === parseInt(levelForm.degreeId));
      if (!selectedDegree) throw new Error('Département non trouvé');
      
      const response = await fetch(`https://unwavering-luck-2076e754ea.strapiapp.com/api/levels/${selectedLevel.documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            name: levelForm.name,
            order: levelForm.order,
            degree: {
              connect: [selectedDegree.documentId]
            }
          }
        })
      });
      
      if (response.ok) {
        setFormSuccess(`Niveau "${levelForm.name}" mis à jour`);
        setShowEditLevelModal(false);
        setSelectedLevel(null);
        fetchAllData();
        setTimeout(() => setFormSuccess(''), 3000);
      } else {
        const error = await response.json();
        throw new Error(error.error?.message || "Erreur lors de la mise à jour");
      }
    } catch (error) {
      setFormError(error.message);
      setTimeout(() => setFormError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLevel = async (documentId, levelName) => {
    if (!window.confirm(`Supprimer le niveau "${levelName}" ? Toutes les matières associées seront affectées.`)) return;
    try {
      await fetch(`https://unwavering-luck-2076e754ea.strapiapp.com/api/levels/${documentId}`, { method: 'DELETE' });
      setFormSuccess(`Niveau "${levelName}" supprimé`);
      fetchAllData();
      setTimeout(() => setFormSuccess(''), 2000);
    } catch (error) {
      setFormError("Erreur lors de la suppression");
      setTimeout(() => setFormError(''), 3000);
    }
  };

  const openEditLevelModal = (level) => {
    setSelectedLevel(level);
    setLevelForm({
      name: level.name,
      degreeId: level.degree?.id?.toString() || '',
      order: level.order || 1
    });
    setShowEditLevelModal(true);
  };

  // ===== DEGREE CRUD =====
  const handleAddDegree = async (e) => {
    e.preventDefault();
    if (!degreeForm.name || !degreeForm.programId) {
      setFormError('Nom et programme requis');
      setTimeout(() => setFormError(''), 3000);
      return;
    }
    
    try {
      setLoading(true);
      
      const selectedProgram = programs.find(p => p.id === parseInt(degreeForm.programId));
      if (!selectedProgram) throw new Error('Programme non trouvé');
      
      const response = await fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/degrees', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            name: degreeForm.name,
            order: degreeForm.order,
            program: {
              connect: [selectedProgram.documentId]
            }
          }
        })
      });
      
      if (response.ok) {
        setFormSuccess(`Département "${degreeForm.name}" ajouté`);
        setDegreeForm({ name: '', programId: '', order: 1 });
        setShowDegreeModal(false);
        fetchAllData();
        setTimeout(() => setFormSuccess(''), 3000);
      } else {
        const error = await response.json();
        throw new Error(error.error?.message || "Erreur lors de l'ajout");
      }
    } catch (error) {
      setFormError(error.message);
      setTimeout(() => setFormError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDegree = async (e) => {
    e.preventDefault();
    if (!selectedDegree) return;
    
    try {
      setLoading(true);
      
      const selectedProgram = programs.find(p => p.id === parseInt(degreeForm.programId));
      if (!selectedProgram) throw new Error('Programme non trouvé');
      
      const response = await fetch(`https://unwavering-luck-2076e754ea.strapiapp.com/api/degrees/${selectedDegree.documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            name: degreeForm.name,
            order: degreeForm.order,
            program: {
              connect: [selectedProgram.documentId]
            }
          }
        })
      });
      
      if (response.ok) {
        setFormSuccess(`Département "${degreeForm.name}" mis à jour`);
        setShowEditDegreeModal(false);
        setSelectedDegree(null);
        fetchAllData();
        setTimeout(() => setFormSuccess(''), 3000);
      } else {
        const error = await response.json();
        throw new Error(error.error?.message || "Erreur lors de la mise à jour");
      }
    } catch (error) {
      setFormError(error.message);
      setTimeout(() => setFormError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDegree = async (documentId, degreeName) => {
    if (!window.confirm(`Supprimer le département "${degreeName}" ? Tous les niveaux associés seront affectés.`)) return;
    try {
      await fetch(`https://unwavering-luck-2076e754ea.strapiapp.com/api/degrees/${documentId}`, { method: 'DELETE' });
      setFormSuccess(`Département "${degreeName}" supprimé`);
      fetchAllData();
      setTimeout(() => setFormSuccess(''), 2000);
    } catch (error) {
      setFormError("Erreur lors de la suppression");
      setTimeout(() => setFormError(''), 3000);
    }
  };

  const openEditDegreeModal = (degree) => {
    setSelectedDegree(degree);
    setDegreeForm({
      name: degree.name,
      programId: degree.program?.id?.toString() || '',
      order: degree.order || 1
    });
    setShowEditDegreeModal(true);
  };

  // ===== PROGRAM CRUD =====
  const handleAddProgram = async (e) => {
    e.preventDefault();
    if (!programForm.name) {
      setFormError('Nom requis');
      setTimeout(() => setFormError(''), 3000);
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/programs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: { name: programForm.name, order: programForm.order }
        })
      });
      
      if (response.ok) {
        setFormSuccess(`Programme "${programForm.name}" ajouté`);
        setProgramForm({ name: '', order: 1 });
        setShowProgramModal(false);
        fetchAllData();
        setTimeout(() => setFormSuccess(''), 3000);
      } else {
        const error = await response.json();
        throw new Error(error.error?.message || "Erreur lors de l'ajout");
      }
    } catch (error) {
      setFormError(error.message);
      setTimeout(() => setFormError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProgram = async (e) => {
    e.preventDefault();
    if (!selectedProgram) return;
    
    try {
      setLoading(true);
      
      const response = await fetch(`https://unwavering-luck-2076e754ea.strapiapp.com/api/programs/${selectedProgram.documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: { name: programForm.name, order: programForm.order }
        })
      });
      
      if (response.ok) {
        setFormSuccess(`Programme "${programForm.name}" mis à jour`);
        setShowEditProgramModal(false);
        setSelectedProgram(null);
        fetchAllData();
        setTimeout(() => setFormSuccess(''), 3000);
      } else {
        const error = await response.json();
        throw new Error(error.error?.message || "Erreur lors de la mise à jour");
      }
    } catch (error) {
      setFormError(error.message);
      setTimeout(() => setFormError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProgram = async (documentId, programName) => {
    if (!window.confirm(`Supprimer le programme "${programName}" ? Tous les départements associés seront affectés.`)) return;
    try {
      await fetch(`https://unwavering-luck-2076e754ea.strapiapp.com/api/programs/${documentId}`, { method: 'DELETE' });
      setFormSuccess(`Programme "${programName}" supprimé`);
      fetchAllData();
      setTimeout(() => setFormSuccess(''), 2000);
    } catch (error) {
      setFormError("Erreur lors de la suppression");
      setTimeout(() => setFormError(''), 3000);
    }
  };

  const openEditProgramModal = (program) => {
    setSelectedProgram(program);
    setProgramForm({
      name: program.name,
      order: program.order || 1
    });
    setShowEditProgramModal(true);
  };

  // ===== CORRECTION LINK =====
  const handleLinkCorrection = async () => {
    if (!linkForm.sourceId || !linkForm.targetId) {
      setFormError('Sélectionnez un document et une correction');
      setTimeout(() => setFormError(''), 3000);
      return;
    }
    
    try {
      setLoading(true);
      
      const sourceResource = resources.find(r => r.id === parseInt(linkForm.sourceId));
      const targetResource = resources.find(r => r.id === parseInt(linkForm.targetId));
      
      if (!sourceResource) throw new Error('Document source non trouvé');
      if (!targetResource) throw new Error('Correction non trouvée');
      
      const response = await fetch(`https://unwavering-luck-2076e754ea.strapiapp.com/api/resources/${sourceResource.documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: { correction: targetResource.id }
        })
      });
      
      if (response.ok) {
        setFormSuccess('Correction liée avec succès');
        setLinkForm({ sourceId: '', targetId: '' });
        setShowLinkCorrectionModal(false);
        fetchAllData();
        setTimeout(() => setFormSuccess(''), 2000);
      } else {
        throw new Error("Erreur lors du lien");
      }
    } catch (error) {
      console.error('Error:', error);
      setFormError(error.message || "Erreur de connexion");
      setTimeout(() => setFormError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveResource = async (documentId) => {
    try {
      const response = await fetch(`https://unwavering-luck-2076e754ea.strapiapp.com/api/resources/${documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: { approvalStatus: 'approved' } })
      });
      
      if (response.ok) {
        setFormSuccess('Ressource approuvée');
        fetchAllData();
        setTimeout(() => setFormSuccess(''), 2000);
      }
    } catch (error) {
      setFormError("Erreur lors de l'approbation");
      setTimeout(() => setFormError(''), 3000);
    }
  };

  const handleRejectResource = async (documentId) => {
    if (!window.confirm('Rejeter cette ressource ? Elle sera supprimée définitivement.')) return;
    try {
      await fetch(`https://unwavering-luck-2076e754ea.strapiapp.com/api/resources/${documentId}`, { method: 'DELETE' });
      setFormSuccess('Ressource rejetée et supprimée');
      fetchAllData();
      setTimeout(() => setFormSuccess(''), 2000);
    } catch (error) {
      setFormError("Erreur lors du rejet");
      setTimeout(() => setFormError(''), 3000);
    }
  };

  const openEditModal = (resource) => {
    setSelectedResource(resource);
    setEditForm({
      title: resource.title,
      type: resource.type,
      subjectId: resource.subject?.toString() || '',
      file: null
    });
    setShowEditResourceModal(true);
  };

  const getTypeColor = (type) => {
    const colors = {
      Cours: 'border-mint/30 text-mint bg-mint/5',
      Examens: 'border-pink/30 text-pink bg-pink/5',
      Correction: 'border-yellow/30 text-yellow bg-yellow/5',
      TP: 'border-yellow/30 text-yellow bg-yellow/5',
      TD: 'border-orange/30 text-orange bg-orange/5',
      Projets: 'border-purple/30 text-purple bg-purple/5'
    };
    return colors[type] || 'border-white/20 text-white/40';
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center px-6">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="bg-gradient-to-r from-mint to-pink w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Archive size={32} className="text-dark-bg" />
            </div>
            <h1 className="text-2xl font-serif text-white">Accès Administrateur</h1>
            <p className="text-white/40 text-sm mt-2">Zone sécurisée - Accès restreint</p>
          </div>
          
          <form onSubmit={handleLogin} className="bg-dark-surface/50 border border-white/10 rounded-2xl p-6">
            <div className="mb-4">
              <label className="block text-white/60 text-sm font-mono mb-2">Mot de passe</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint"
                autoFocus
              />
            </div>
            {loginError && (
              <div className="mb-4 p-3 bg-pink/10 border border-pink/30 rounded-xl flex items-center gap-2">
                <AlertCircle size={16} className="text-pink" />
                <span className="text-pink text-xs">Mot de passe incorrect</span>
              </div>
            )}
            <button type="submit" className="w-full bg-gradient-to-r from-mint to-pink text-dark-bg py-3 rounded-xl font-mono text-sm font-semibold hover:shadow-lg transition">
              Se connecter
            </button>
          </form>
        </div>
      </div>
    );
  }

  const filteredPending = pendingResources.filter(r => !searchTerm || r.title?.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredApproved = approvedResources.filter(r => !searchTerm || r.title?.toLowerCase().includes(searchTerm.toLowerCase()));
  const resourcesNeedingCorrection = approvedResources.filter(r => 
    typesThatCanHaveCorrection.includes(r.type) && !r.correction
  );
  const availableCorrections = approvedResources.filter(r => r.type === 'Correction');

  return (
    <div className="bg-dark-bg min-h-screen">
      {/* Header */}
      <div className="border-b border-white/10 bg-dark-surface/50 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-r from-mint to-pink w-8 h-8 rounded-lg flex items-center justify-center">
                <Archive size={16} className="text-dark-bg" />
              </div>
              <Link to="/" className="font-mono text-sm text-white hover:text-mint transition">ISIMM_Archive</Link>
              <span className="text-white/20">/</span>
              <span className="font-mono text-sm bg-gradient-to-r from-mint to-pink bg-clip-text text-transparent">Administration</span>
            </div>
            <div className="flex items-center gap-4">
              <button onClick={fetchAllData} className="text-white/40 hover:text-mint transition"><RefreshCw size={16} /></button>
              <button onClick={() => setIsAuthenticated(false)} className="font-mono text-xs text-white/40 hover:text-pink transition">Déconnexion</button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Messages */}
        {formSuccess && (
          <div className="mb-6 p-4 bg-mint/10 border border-mint/30 rounded-xl flex items-center gap-2 animate-fade-up">
            <CheckCircle size={18} className="text-mint" />
            <span className="text-mint text-sm">{formSuccess}</span>
          </div>
        )}
        {formError && (
          <div className="mb-6 p-4 bg-pink/10 border border-pink/30 rounded-xl flex items-center gap-2 animate-fade-up">
            <AlertCircle size={18} className="text-pink" />
            <span className="text-pink text-sm">{formError}</span>
          </div>
        )}

        {/* Search */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Rechercher une ressource..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-3 bg-dark-surface border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-mint"
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-white/10 pb-4 overflow-x-auto">
          <button onClick={() => setActiveTab('pending')} className={`px-5 py-2 rounded-xl font-mono text-sm transition flex items-center gap-2 ${activeTab === 'pending' ? 'bg-gradient-to-r from-mint to-pink text-dark-bg' : 'text-white/60 hover:text-white'}`}>
            <Clock size={14} /> En attente ({pendingResources.length})
          </button>
          <button onClick={() => setActiveTab('resources')} className={`px-5 py-2 rounded-xl font-mono text-sm transition flex items-center gap-2 ${activeTab === 'resources' ? 'bg-gradient-to-r from-mint to-pink text-dark-bg' : 'text-white/60 hover:text-white'}`}>
            <FileText size={14} /> Approuvées ({approvedResources.length})
          </button>
          <button onClick={() => setActiveTab('corrections')} className={`px-5 py-2 rounded-xl font-mono text-sm transition flex items-center gap-2 ${activeTab === 'corrections' ? 'bg-gradient-to-r from-mint to-pink text-dark-bg' : 'text-white/60 hover:text-white'}`}>
            <Link2 size={14} /> Lier corrections
          </button>
          <button onClick={() => setActiveTab('subjects')} className={`px-5 py-2 rounded-xl font-mono text-sm transition flex items-center gap-2 ${activeTab === 'subjects' ? 'bg-gradient-to-r from-mint to-pink text-dark-bg' : 'text-white/60 hover:text-white'}`}>
            <Book size={14} /> Matières ({subjects.length})
          </button>
          <button onClick={() => setActiveTab('levels')} className={`px-5 py-2 rounded-xl font-mono text-sm transition flex items-center gap-2 ${activeTab === 'levels' ? 'bg-gradient-to-r from-mint to-pink text-dark-bg' : 'text-white/60 hover:text-white'}`}>
            <Layers size={14} /> Niveaux ({levels.length})
          </button>
          <button onClick={() => setActiveTab('degrees')} className={`px-5 py-2 rounded-xl font-mono text-sm transition flex items-center gap-2 ${activeTab === 'degrees' ? 'bg-gradient-to-r from-mint to-pink text-dark-bg' : 'text-white/60 hover:text-white'}`}>
            <GraduationCap size={14} /> Départements ({degrees.length})
          </button>
          <button onClick={() => setActiveTab('programs')} className={`px-5 py-2 rounded-xl font-mono text-sm transition flex items-center gap-2 ${activeTab === 'programs' ? 'bg-gradient-to-r from-mint to-pink text-dark-bg' : 'text-white/60 hover:text-white'}`}>
            <Tag size={14} /> Programmes ({programs.length})
          </button>
        </div>

        {/* PENDING TAB */}
        {activeTab === 'pending' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-serif text-white">Soumissions en attente</h2>
                <p className="text-white/40 text-sm mt-1">Documents soumis par les utilisateurs en attente de validation</p>
              </div>
              <button onClick={() => setShowResourceModal(true)} className="flex items-center gap-2 bg-gradient-to-r from-mint to-pink text-dark-bg px-4 py-2 rounded-xl font-mono text-sm font-semibold hover:shadow-lg">
                <Plus size={16} /> Ajouter directement
              </button>
            </div>
            
            {filteredPending.length === 0 ? (
              <div className="text-center py-16 bg-dark-surface/30 rounded-2xl border border-white/10">
                <CheckCircle size={48} className="text-mint mx-auto mb-3" />
                <p className="text-white/40">Aucune soumission en attente</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredPending.map(resource => (
                  <div key={resource.documentId} className="bg-dark-surface/50 border border-yellow/30 rounded-2xl p-5">
                    <div className="flex justify-between items-start flex-wrap gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3 flex-wrap">
                          <h3 className="font-mono text-lg text-white">{resource.title}</h3>
                          <span className={`px-2 py-1 rounded-lg text-xs font-mono border ${getTypeColor(resource.type)}`}>
                            {resource.type}
                          </span>
                          <span className="bg-yellow/20 text-yellow text-xs px-2 py-1 rounded-full flex items-center gap-1">
                            <Clock size={10} /> En attente
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                          <p className="text-white/40"><span className="text-white/30 text-xs">Date:</span> {resource.createdAt?.split('T')[0]}</p>
                          <p className="text-white/40"><span className="text-white/30 text-xs">Matière:</span> {resource.subjectName || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => handleApproveResource(resource.documentId)} className="p-2 rounded-xl bg-mint/20 hover:bg-mint/30 transition-all" title="Approuver">
                          <Check size={18} className="text-mint" />
                        </button>
                        <button onClick={() => handleRejectResource(resource.documentId)} className="p-2 rounded-xl bg-pink/20 hover:bg-pink/30 transition-all" title="Rejeter">
                          <XCircle size={18} className="text-pink" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* APPROVED RESOURCES TAB */}
        {activeTab === 'resources' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-serif text-white">Ressources approuvées</h2>
                <p className="text-white/40 text-sm mt-1">{approvedResources.length} documents disponibles dans l'archive</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setShowLinkCorrectionModal(true)} className="flex items-center gap-2 bg-yellow/20 text-yellow px-4 py-2 rounded-xl font-mono text-sm hover:bg-yellow/30 transition">
                  <Link2 size={16} /> Lier correction
                </button>
                <button onClick={() => setShowResourceModal(true)} className="flex items-center gap-2 bg-gradient-to-r from-mint to-pink text-dark-bg px-4 py-2 rounded-xl font-mono text-sm font-semibold hover:shadow-lg">
                  <Plus size={16} /> Ajouter
                </button>
              </div>
            </div>
            
            <div className="space-y-3">
              {filteredApproved.map(resource => (
                <div key={resource.documentId} className="bg-dark-surface/50 border border-white/10 rounded-2xl p-4 flex items-center justify-between group hover:border-mint/30 transition-all">
                  <div className="flex items-center gap-4 flex-1">
                    <FileText size={20} className="text-mint" />
                    <div className="flex-1">
                      <h3 className="font-mono text-sm text-white">{resource.title}</h3>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className={`font-mono text-[9px] px-2 py-0.5 rounded border ${getTypeColor(resource.type)}`}>
                          {resource.type}
                        </span>
                        <span className="font-mono text-[9px] text-white/30">{resource.createdAt?.split('T')[0]}</span>
                        <span className="font-mono text-[9px] text-white/30">Matière: {resource.subjectName || 'N/A'}</span>
                        {resource.hasCorrection && (
                          <span className="font-mono text-[9px] text-yellow bg-yellow/10 px-2 py-0.5 rounded">Correction liée</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => openEditModal(resource)} className="p-2 rounded-xl hover:bg-white/5 transition-all" title="Modifier">
                      <Edit size={16} className="text-white/40 hover:text-mint" />
                    </button>
                    <button onClick={() => handleDeleteResource(resource.documentId)} className="p-2 rounded-xl hover:bg-white/5 transition-all" title="Supprimer">
                      <Trash2 size={16} className="text-white/40 hover:text-pink" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* LINK CORRECTIONS TAB */}
        {activeTab === 'corrections' && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-serif text-white">Lier des corrections</h2>
              <p className="text-white/40 text-sm mt-1">Associez une correction à un examen, TD ou TP</p>
            </div>
            
            <div className="bg-dark-surface/50 border border-white/10 rounded-2xl p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Document (Examen, TD, TP)</label>
                  <select
                    value={linkForm.sourceId}
                    onChange={(e) => setLinkForm({ ...linkForm, sourceId: e.target.value })}
                    className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint"
                  >
                    <option value="">Sélectionner un document</option>
                    {resourcesNeedingCorrection.map(res => (
                      <option key={res.id} value={res.id}>{res.title} ({res.type})</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Correction</label>
                  <select
                    value={linkForm.targetId}
                    onChange={(e) => setLinkForm({ ...linkForm, targetId: e.target.value })}
                    className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint"
                  >
                    <option value="">Sélectionner une correction</option>
                    {availableCorrections.map(cor => (
                      <option key={cor.id} value={cor.id}>{cor.title}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <button
                onClick={handleLinkCorrection}
                disabled={!linkForm.sourceId || !linkForm.targetId}
                className="bg-gradient-to-r from-mint to-pink text-dark-bg px-6 py-2 rounded-xl font-mono text-sm hover:shadow-lg transition disabled:opacity-50"
              >
                <Link2 size={14} className="inline mr-2" /> Lier la correction
              </button>
            </div>
          </div>
        )}

        {/* SUBJECTS TAB */}
        {activeTab === 'subjects' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-serif text-white">Matières</h2>
                <p className="text-white/40 text-sm mt-1">Gérez les matières enseignées dans chaque niveau</p>
              </div>
              <button onClick={() => setShowSubjectModal(true)} className="flex items-center gap-2 bg-gradient-to-r from-mint to-pink text-dark-bg px-4 py-2 rounded-xl font-mono text-sm font-semibold hover:shadow-lg">
                <Plus size={16} /> Ajouter une matière
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {subjects.map(subject => {
                const level = levels.find(l => l.id === subject.level?.id);
                const degree = degrees.find(d => d.id === level?.degree?.id);
                const program = programs.find(p => p.id === degree?.program?.id);
                return (
                  <div key={subject.documentId} className="bg-dark-surface/50 border border-white/10 rounded-2xl p-4 hover:border-mint/30 transition-all">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-mono text-white font-semibold">{subject.name}</h3>
                        <div className="mt-3 space-y-1">
                          <p className="text-white/40 text-xs flex items-center gap-2">
                            <Layers size={10} className="text-mint" />
                            Niveau: {level?.name || 'N/A'}
                          </p>
                          <p className="text-white/40 text-xs flex items-center gap-2">
                            <GraduationCap size={10} className="text-pink" />
                            Département: {degree?.name || 'N/A'}
                          </p>
                          <p className="text-white/40 text-xs flex items-center gap-2">
                            <Tag size={10} className="text-orange" />
                            Programme: {program?.name || 'N/A'}
                          </p>
                          <p className="text-white/30 text-xs flex items-center gap-2">
                            <Calendar size={10} className="text-yellow" />
                            Semestre: {subject.semester}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-2">
                        <button 
                          onClick={() => openEditSubjectModal(subject)}
                          className="text-white/40 hover:text-mint transition p-1"
                          title="Modifier"
                        >
                          <Edit size={16} />
                        </button>
                        <button 
                          onClick={() => handleDeleteSubject(subject.documentId, subject.name)}
                          className="text-white/40 hover:text-pink transition p-1"
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* LEVELS TAB */}
        {activeTab === 'levels' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-serif text-white">Niveaux</h2>
                <p className="text-white/40 text-sm mt-1">Années d'étude (INF1, MATH2, CPI1, etc.)</p>
              </div>
              <button onClick={() => setShowLevelModal(true)} className="flex items-center gap-2 bg-gradient-to-r from-mint to-pink text-dark-bg px-4 py-2 rounded-xl font-mono text-sm font-semibold hover:shadow-lg">
                <Plus size={16} /> Ajouter un niveau
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {levels.map(level => {
                const degree = degrees.find(d => d.id === level.degree?.id);
                const program = programs.find(p => p.id === degree?.program?.id);
                return (
                  <div key={level.documentId} className="bg-dark-surface/50 border border-white/10 rounded-2xl p-4 text-center hover:border-mint/30 transition-all">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <h3 className="font-mono text-white text-lg">{level.name}</h3>
                        <p className="text-white/40 text-xs mt-1">{degree?.name || 'N/A'}</p>
                        <p className="text-white/30 text-[10px] mt-1">{program?.name || 'N/A'}</p>
                      </div>
                      <div className="flex gap-1">
                        <button 
                          onClick={() => openEditLevelModal(level)}
                          className="text-white/40 hover:text-mint transition p-1"
                          title="Modifier"
                        >
                          <Edit size={14} />
                        </button>
                        <button 
                          onClick={() => handleDeleteLevel(level.documentId, level.name)}
                          className="text-white/40 hover:text-pink transition p-1"
                          title="Supprimer"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* DEGREES TAB */}
        {activeTab === 'degrees' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-serif text-white">Départements</h2>
                <p className="text-white/40 text-sm mt-1">INFO, MATH, TIC, EEA, CPI1, CPI2...</p>
              </div>
              <button onClick={() => setShowDegreeModal(true)} className="flex items-center gap-2 bg-gradient-to-r from-mint to-pink text-dark-bg px-4 py-2 rounded-xl font-mono text-sm font-semibold hover:shadow-lg">
                <Plus size={16} /> Ajouter un département
              </button>
            </div>
            <div className="flex flex-wrap gap-3">
              {degrees.map(degree => {
                const program = programs.find(p => p.id === degree.program?.id);
                return (
                  <div key={degree.documentId} className="bg-dark-surface/50 border border-white/10 rounded-2xl px-5 py-3 hover:border-mint/30 transition-all">
                    <div className="flex justify-between items-center gap-3">
                      <div>
                        <h3 className="font-mono text-white">{degree.name}</h3>
                        <p className="text-white/30 text-[10px] mt-1">{program?.name || 'N/A'}</p>
                      </div>
                      <div className="flex gap-1">
                        <button 
                          onClick={() => openEditDegreeModal(degree)}
                          className="text-white/40 hover:text-mint transition p-1"
                          title="Modifier"
                        >
                          <Edit size={14} />
                        </button>
                        <button 
                          onClick={() => handleDeleteDegree(degree.documentId, degree.name)}
                          className="text-white/40 hover:text-pink transition p-1"
                          title="Supprimer"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* PROGRAMS TAB */}
        {activeTab === 'programs' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-serif text-white">Programmes</h2>
                <p className="text-white/40 text-sm mt-1">Licence, Mastere, Cycle préparatoire...</p>
              </div>
              <button onClick={() => setShowProgramModal(true)} className="flex items-center gap-2 bg-gradient-to-r from-mint to-pink text-dark-bg px-4 py-2 rounded-xl font-mono text-sm font-semibold hover:shadow-lg">
                <Plus size={16} /> Ajouter un programme
              </button>
            </div>
            <div className="flex flex-wrap gap-3">
              {programs.map(program => (
                <div key={program.documentId} className="bg-dark-surface/50 border border-white/10 rounded-2xl px-5 py-3 hover:border-mint/30 transition-all">
                  <div className="flex justify-between items-center gap-3">
                    <h3 className="font-mono text-white">{program.name}</h3>
                    <div className="flex gap-1">
                      <button 
                        onClick={() => openEditProgramModal(program)}
                        className="text-white/40 hover:text-mint transition p-1"
                        title="Modifier"
                      >
                        <Edit size={14} />
                      </button>
                      <button 
                        onClick={() => handleDeleteProgram(program.documentId, program.name)}
                        className="text-white/40 hover:text-pink transition p-1"
                        title="Supprimer"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ===== MODALS ===== */}

        {/* ADD RESOURCE MODAL */}
        {showResourceModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark-bg/90 backdrop-blur-md">
            <div className="bg-dark-surface border border-white/10 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-6 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-r from-mint to-pink w-10 h-10 rounded-xl flex items-center justify-center"><Plus size={18} className="text-dark-bg" /></div>
                  <div><h2 className="text-xl font-serif text-white">Ajouter une ressource</h2></div>
                </div>
                <button onClick={() => setShowResourceModal(false)} className="text-white/40 hover:text-white"><X size={20} /></button>
              </div>
              
              <form onSubmit={handleAddResource} className="p-6 space-y-5">
                <div>
                  <label className="block text-white/70 text-sm font-mono mb-2">Titre *</label>
                  <input type="text" value={resourceForm.title} onChange={(e) => setResourceForm({ ...resourceForm, title: e.target.value })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint" required />
                </div>

                <div>
                  <label className="block text-white/70 text-sm font-mono mb-2">Type *</label>
                  <div className="grid grid-cols-6 gap-2">
                    {resourceTypes.map(type => (
                      <button key={type.id} type="button" onClick={() => setResourceForm({ ...resourceForm, type: type.id })} className={`p-3 rounded-xl text-center transition ${resourceForm.type === type.id ? `bg-${type.color} text-dark-bg` : 'bg-dark-card border border-white/10 text-white/60'}`}>
                        <div className="flex flex-col items-center gap-1">{type.icon}<span className="font-mono text-[10px]">{type.name}</span></div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-white/70 text-sm font-mono mb-2">Programme *</label>
                  <select value={resourceForm.programId} onChange={(e) => setResourceForm({ ...resourceForm, programId: e.target.value, degreeId: '', levelId: '', subjectId: '' })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint" required>
                    <option value="">Sélectionner</option>
                    {programs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>

                {resourceForm.programId && (
                  <div>
                    <label className="block text-white/70 text-sm font-mono mb-2">Département *</label>
                    <select value={resourceForm.degreeId} onChange={(e) => setResourceForm({ ...resourceForm, degreeId: e.target.value, levelId: '', subjectId: '' })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint" required>
                      <option value="">Sélectionner</option>
                      {availableDegrees.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                )}

                {resourceForm.degreeId && (
                  <div>
                    <label className="block text-white/70 text-sm font-mono mb-2">Niveau *</label>
                    <select value={resourceForm.levelId} onChange={(e) => setResourceForm({ ...resourceForm, levelId: e.target.value, subjectId: '' })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint" required>
                      <option value="">Sélectionner</option>
                      {availableLevels.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  </div>
                )}

                {resourceForm.levelId && (
                  <div>
                    <label className="block text-white/70 text-sm font-mono mb-2">Matière *</label>
                    <select value={resourceForm.subjectId} onChange={(e) => setResourceForm({ ...resourceForm, subjectId: e.target.value })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint" required>
                      <option value="">Sélectionner</option>
                      {availableSubjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-white/70 text-sm font-mono mb-2">Fichier PDF *</label>
                  <div className="border-2 border-dashed border-white/20 rounded-xl p-6 text-center hover:border-mint/50 transition cursor-pointer bg-dark-card/30">
                    <input type="file" accept=".pdf" onChange={(e) => setResourceForm({ ...resourceForm, file: e.target.files[0] })} className="hidden" id="resourceFile" />
                    <label htmlFor="resourceFile" className="cursor-pointer">
                      {resourceForm.file ? <div className="flex items-center justify-center gap-3"><FileText size={24} className="text-mint" /><span className="text-white text-sm">{resourceForm.file.name}</span></div> : <div><Upload size={28} className="text-mint mx-auto mb-2" /><p className="text-white/60 text-sm">Cliquez pour sélectionner un PDF</p></div>}
                    </label>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowResourceModal(false)} className="flex-1 px-4 py-3 border border-white/20 rounded-xl text-white/60 hover:border-white/40 transition">Annuler</button>
                  <button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-mint to-pink text-dark-bg px-4 py-3 rounded-xl font-mono text-sm font-semibold hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2">
                    {loading ? <div className="w-4 h-4 border-2 border-dark-bg border-t-transparent rounded-full animate-spin" /> : <><ArrowRight size={14} /> Ajouter</>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* EDIT RESOURCE MODAL */}
        {showEditResourceModal && selectedResource && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark-bg/90 backdrop-blur-md">
            <div className="bg-dark-surface border border-white/10 rounded-2xl max-w-md w-full">
              <div className="flex justify-between items-center p-6 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-r from-mint to-pink w-10 h-10 rounded-xl flex items-center justify-center"><Edit size={18} className="text-dark-bg" /></div>
                  <div><h2 className="text-xl font-serif text-white">Modifier la ressource</h2></div>
                </div>
                <button onClick={() => setShowEditResourceModal(false)} className="text-white/40 hover:text-white"><X size={20} /></button>
              </div>
              
              <form onSubmit={handleUpdateResource} className="p-6 space-y-4">
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Titre *</label>
                  <input type="text" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint" required />
                </div>

                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Type *</label>
                  <select value={editForm.type} onChange={(e) => setEditForm({ ...editForm, type: e.target.value })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint">
                    {resourceTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Matière *</label>
                  <select value={editForm.subjectId} onChange={(e) => setEditForm({ ...editForm, subjectId: e.target.value })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint" required>
                    <option value="">Sélectionner</option>
                    {subjects.map(s => <option key={s.id} value={s.id}>{s.name} ({s.semester})</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Nouveau fichier PDF (optionnel)</label>
                  <div className="border-2 border-dashed border-white/20 rounded-xl p-4 text-center hover:border-mint/50 transition cursor-pointer bg-dark-card/30">
                    <input type="file" accept=".pdf" onChange={(e) => setEditForm({ ...editForm, file: e.target.files[0] })} className="hidden" id="editFile" />
                    <label htmlFor="editFile" className="cursor-pointer">
                      {editForm.file ? <div className="flex items-center justify-center gap-2"><FileText size={20} className="text-mint" /><span className="text-white text-xs">{editForm.file.name}</span></div> : <div><Upload size={24} className="text-mint mx-auto mb-1" /><p className="text-white/40 text-xs">Cliquez pour remplacer le PDF</p></div>}
                    </label>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowEditResourceModal(false)} className="flex-1 px-4 py-3 border border-white/20 rounded-xl text-white/60 hover:border-white/40 transition">Annuler</button>
                  <button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-mint to-pink text-dark-bg px-4 py-3 rounded-xl font-mono text-sm font-semibold hover:shadow-lg flex items-center justify-center gap-2">
                    {loading ? <div className="w-4 h-4 border-2 border-dark-bg border-t-transparent rounded-full animate-spin" /> : <><Save size={14} /> Enregistrer</>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ADD SUBJECT MODAL */}
        {showSubjectModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark-bg/90 backdrop-blur-md">
            <div className="bg-dark-surface border border-white/10 rounded-2xl max-w-md w-full">
              <div className="flex justify-between items-center p-6 border-b border-white/10">
                <h2 className="text-xl font-serif text-white">Ajouter une matière</h2>
                <button onClick={() => setShowSubjectModal(false)} className="text-white/40 hover:text-white"><X size={20} /></button>
              </div>
              <form onSubmit={handleAddSubject} className="p-6 space-y-4">
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Nom *</label>
                  <input type="text" value={subjectForm.name} onChange={(e) => setSubjectForm({ ...subjectForm, name: e.target.value })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint" required />
                </div>
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Niveau *</label>
                  <select value={subjectForm.levelId} onChange={(e) => setSubjectForm({ ...subjectForm, levelId: e.target.value })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint" required>
                    <option value="">Sélectionner</option>
                    {levels.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Semestre</label>
                  <select value={subjectForm.semester} onChange={(e) => setSubjectForm({ ...subjectForm, semester: e.target.value })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white">
                    <option value="Semestre 1">Semestre 1</option>
                    <option value="Semestre 2">Semestre 2</option>
                  </select>
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowSubjectModal(false)} className="flex-1 px-4 py-3 border border-white/20 rounded-xl text-white/60 hover:border-white/40 transition">Annuler</button>
                  <button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-mint to-pink text-dark-bg px-4 py-3 rounded-xl font-mono text-sm font-semibold hover:shadow-lg transition">Ajouter</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* EDIT SUBJECT MODAL */}
        {showEditSubjectModal && selectedSubject && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark-bg/90 backdrop-blur-md">
            <div className="bg-dark-surface border border-white/10 rounded-2xl max-w-md w-full">
              <div className="flex justify-between items-center p-6 border-b border-white/10">
                <h2 className="text-xl font-serif text-white">Modifier la matière</h2>
                <button onClick={() => setShowEditSubjectModal(false)} className="text-white/40 hover:text-white"><X size={20} /></button>
              </div>
              <form onSubmit={handleUpdateSubject} className="p-6 space-y-4">
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Nom *</label>
                  <input type="text" value={subjectForm.name} onChange={(e) => setSubjectForm({ ...subjectForm, name: e.target.value })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint" required />
                </div>
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Niveau *</label>
                  <select value={subjectForm.levelId} onChange={(e) => setSubjectForm({ ...subjectForm, levelId: e.target.value })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint" required>
                    <option value="">Sélectionner</option>
                    {levels.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Semestre</label>
                  <select value={subjectForm.semester} onChange={(e) => setSubjectForm({ ...subjectForm, semester: e.target.value })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white">
                    <option value="Semestre 1">Semestre 1</option>
                    <option value="Semestre 2">Semestre 2</option>
                  </select>
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowEditSubjectModal(false)} className="flex-1 px-4 py-3 border border-white/20 rounded-xl text-white/60 hover:border-white/40 transition">Annuler</button>
                  <button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-mint to-pink text-dark-bg px-4 py-3 rounded-xl font-mono text-sm font-semibold hover:shadow-lg transition">Enregistrer</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ADD LEVEL MODAL */}
        {showLevelModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark-bg/90 backdrop-blur-md">
            <div className="bg-dark-surface border border-white/10 rounded-2xl max-w-md w-full">
              <div className="flex justify-between items-center p-6 border-b border-white/10">
                <h2 className="text-xl font-serif text-white">Ajouter un niveau</h2>
                <button onClick={() => setShowLevelModal(false)} className="text-white/40 hover:text-white"><X size={20} /></button>
              </div>
              <form onSubmit={handleAddLevel} className="p-6 space-y-4">
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Nom *</label>
                  <input type="text" value={levelForm.name} onChange={(e) => setLevelForm({ ...levelForm, name: e.target.value })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint" placeholder="Ex: INF1" required />
                </div>
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Département *</label>
                  <select value={levelForm.degreeId} onChange={(e) => setLevelForm({ ...levelForm, degreeId: e.target.value })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint" required>
                    <option value="">Sélectionner</option>
                    {degrees.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Ordre</label>
                  <input type="number" value={levelForm.order} onChange={(e) => setLevelForm({ ...levelForm, order: parseInt(e.target.value) })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowLevelModal(false)} className="flex-1 px-4 py-3 border border-white/20 rounded-xl text-white/60 hover:border-white/40 transition">Annuler</button>
                  <button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-mint to-pink text-dark-bg px-4 py-3 rounded-xl font-mono text-sm font-semibold hover:shadow-lg transition">Ajouter</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* EDIT LEVEL MODAL */}
        {showEditLevelModal && selectedLevel && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark-bg/90 backdrop-blur-md">
            <div className="bg-dark-surface border border-white/10 rounded-2xl max-w-md w-full">
              <div className="flex justify-between items-center p-6 border-b border-white/10">
                <h2 className="text-xl font-serif text-white">Modifier le niveau</h2>
                <button onClick={() => setShowEditLevelModal(false)} className="text-white/40 hover:text-white"><X size={20} /></button>
              </div>
              <form onSubmit={handleUpdateLevel} className="p-6 space-y-4">
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Nom *</label>
                  <input type="text" value={levelForm.name} onChange={(e) => setLevelForm({ ...levelForm, name: e.target.value })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint" required />
                </div>
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Département *</label>
                  <select value={levelForm.degreeId} onChange={(e) => setLevelForm({ ...levelForm, degreeId: e.target.value })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint" required>
                    <option value="">Sélectionner</option>
                    {degrees.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Ordre</label>
                  <input type="number" value={levelForm.order} onChange={(e) => setLevelForm({ ...levelForm, order: parseInt(e.target.value) })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowEditLevelModal(false)} className="flex-1 px-4 py-3 border border-white/20 rounded-xl text-white/60 hover:border-white/40 transition">Annuler</button>
                  <button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-mint to-pink text-dark-bg px-4 py-3 rounded-xl font-mono text-sm font-semibold hover:shadow-lg transition">Enregistrer</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ADD DEGREE MODAL */}
        {showDegreeModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark-bg/90 backdrop-blur-md">
            <div className="bg-dark-surface border border-white/10 rounded-2xl max-w-md w-full">
              <div className="flex justify-between items-center p-6 border-b border-white/10">
                <h2 className="text-xl font-serif text-white">Ajouter un département</h2>
                <button onClick={() => setShowDegreeModal(false)} className="text-white/40 hover:text-white"><X size={20} /></button>
              </div>
              <form onSubmit={handleAddDegree} className="p-6 space-y-4">
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Nom *</label>
                  <input type="text" value={degreeForm.name} onChange={(e) => setDegreeForm({ ...degreeForm, name: e.target.value })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint" placeholder="Ex: INFO" required />
                </div>
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Programme *</label>
                  <select value={degreeForm.programId} onChange={(e) => setDegreeForm({ ...degreeForm, programId: e.target.value })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint" required>
                    <option value="">Sélectionner</option>
                    {programs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Ordre</label>
                  <input type="number" value={degreeForm.order} onChange={(e) => setDegreeForm({ ...degreeForm, order: parseInt(e.target.value) })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowDegreeModal(false)} className="flex-1 px-4 py-3 border border-white/20 rounded-xl text-white/60 hover:border-white/40 transition">Annuler</button>
                  <button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-mint to-pink text-dark-bg px-4 py-3 rounded-xl font-mono text-sm font-semibold hover:shadow-lg transition">Ajouter</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* EDIT DEGREE MODAL */}
        {showEditDegreeModal && selectedDegree && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark-bg/90 backdrop-blur-md">
            <div className="bg-dark-surface border border-white/10 rounded-2xl max-w-md w-full">
              <div className="flex justify-between items-center p-6 border-b border-white/10">
                <h2 className="text-xl font-serif text-white">Modifier le département</h2>
                <button onClick={() => setShowEditDegreeModal(false)} className="text-white/40 hover:text-white"><X size={20} /></button>
              </div>
              <form onSubmit={handleUpdateDegree} className="p-6 space-y-4">
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Nom *</label>
                  <input type="text" value={degreeForm.name} onChange={(e) => setDegreeForm({ ...degreeForm, name: e.target.value })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint" required />
                </div>
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Programme *</label>
                  <select value={degreeForm.programId} onChange={(e) => setDegreeForm({ ...degreeForm, programId: e.target.value })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint" required>
                    <option value="">Sélectionner</option>
                    {programs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Ordre</label>
                  <input type="number" value={degreeForm.order} onChange={(e) => setDegreeForm({ ...degreeForm, order: parseInt(e.target.value) })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowEditDegreeModal(false)} className="flex-1 px-4 py-3 border border-white/20 rounded-xl text-white/60 hover:border-white/40 transition">Annuler</button>
                  <button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-mint to-pink text-dark-bg px-4 py-3 rounded-xl font-mono text-sm font-semibold hover:shadow-lg transition">Enregistrer</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ADD PROGRAM MODAL */}
        {showProgramModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark-bg/90 backdrop-blur-md">
            <div className="bg-dark-surface border border-white/10 rounded-2xl max-w-md w-full">
              <div className="flex justify-between items-center p-6 border-b border-white/10">
                <h2 className="text-xl font-serif text-white">Ajouter un programme</h2>
                <button onClick={() => setShowProgramModal(false)} className="text-white/40 hover:text-white"><X size={20} /></button>
              </div>
              <form onSubmit={handleAddProgram} className="p-6 space-y-4">
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Nom *</label>
                  <input type="text" value={programForm.name} onChange={(e) => setProgramForm({ ...programForm, name: e.target.value })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint" placeholder="Ex: Licence" required />
                </div>
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Ordre</label>
                  <input type="number" value={programForm.order} onChange={(e) => setProgramForm({ ...programForm, order: parseInt(e.target.value) })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowProgramModal(false)} className="flex-1 px-4 py-3 border border-white/20 rounded-xl text-white/60 hover:border-white/40 transition">Annuler</button>
                  <button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-mint to-pink text-dark-bg px-4 py-3 rounded-xl font-mono text-sm font-semibold hover:shadow-lg transition">Ajouter</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* EDIT PROGRAM MODAL */}
        {showEditProgramModal && selectedProgram && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark-bg/90 backdrop-blur-md">
            <div className="bg-dark-surface border border-white/10 rounded-2xl max-w-md w-full">
              <div className="flex justify-between items-center p-6 border-b border-white/10">
                <h2 className="text-xl font-serif text-white">Modifier le programme</h2>
                <button onClick={() => setShowEditProgramModal(false)} className="text-white/40 hover:text-white"><X size={20} /></button>
              </div>
              <form onSubmit={handleUpdateProgram} className="p-6 space-y-4">
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Nom *</label>
                  <input type="text" value={programForm.name} onChange={(e) => setProgramForm({ ...programForm, name: e.target.value })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint" required />
                </div>
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Ordre</label>
                  <input type="number" value={programForm.order} onChange={(e) => setProgramForm({ ...programForm, order: parseInt(e.target.value) })} className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowEditProgramModal(false)} className="flex-1 px-4 py-3 border border-white/20 rounded-xl text-white/60 hover:border-white/40 transition">Annuler</button>
                  <button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-mint to-pink text-dark-bg px-4 py-3 rounded-xl font-mono text-sm font-semibold hover:shadow-lg transition">Enregistrer</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* LINK CORRECTION MODAL */}
        {showLinkCorrectionModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark-bg/90 backdrop-blur-md">
            <div className="bg-dark-surface border border-white/10 rounded-2xl max-w-md w-full">
              <div className="flex justify-between items-center p-6 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-r from-yellow to-pink w-10 h-10 rounded-xl flex items-center justify-center"><Link2 size={18} className="text-dark-bg" /></div>
                  <div><h2 className="text-xl font-serif text-white">Lier correction</h2></div>
                </div>
                <button onClick={() => setShowLinkCorrectionModal(false)} className="text-white/40 hover:text-white"><X size={20} /></button>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Document (Examen, TD, TP) *</label>
                  <select
                    value={linkForm.sourceId}
                    onChange={(e) => setLinkForm({ ...linkForm, sourceId: e.target.value })}
                    className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint"
                  >
                    <option value="">Sélectionner un document</option>
                    {resourcesNeedingCorrection.map(res => (
                      <option key={res.id} value={res.id}>{res.title} ({res.type})</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-white/60 text-sm font-mono mb-2">Correction *</label>
                  <select
                    value={linkForm.targetId}
                    onChange={(e) => setLinkForm({ ...linkForm, targetId: e.target.value })}
                    className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint"
                  >
                    <option value="">Sélectionner une correction</option>
                    {availableCorrections.map(cor => (
                      <option key={cor.id} value={cor.id}>{cor.title}</option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowLinkCorrectionModal(false)} className="flex-1 px-4 py-3 border border-white/20 rounded-xl text-white/60 hover:border-white/40 transition">Annuler</button>
                  <button onClick={handleLinkCorrection} disabled={!linkForm.sourceId || !linkForm.targetId} className="flex-1 bg-gradient-to-r from-mint to-pink text-dark-bg px-4 py-3 rounded-xl font-mono text-sm font-semibold hover:shadow-lg disabled:opacity-50">Lier</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-up {
          animation: fadeUp 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}

export default AdminPage;